import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import Avatar from "@/components/Avatar";
import { Users, Music2, X } from "lucide-react";

const HEARTBEAT_MS = 10000;   // POST our state every 10s
const POLL_MS      = 12000;   // GET others' state every 12s

/**
 * Floating "who else is listening right now" pod on the Music page.
 *
 * Pulls online listeners every ~12s and renders avatar bubbles in the
 * bottom-right corner. Click any bubble to jump your hero embed to the song
 * they're listening to (track-jump only — true time-sync is a v2).
 *
 * Heartbeats are POSTed every ~10s with the current track id so this member
 * shows up in everyone else's pod. On unmount we DELETE the row so the bubble
 * disappears the moment you leave the page.
 */
export default function ListenTogether({ currentTrack, onJoin }) {
  const [others, setOthers] = useState([]);
  const [open, setOpen] = useState(true);
  const trackRef = useRef(currentTrack?.id || null);

  // Keep the latest track id in a ref so the heartbeat interval always sends the freshest one.
  useEffect(() => { trackRef.current = currentTrack?.id || null; }, [currentTrack]);

  useEffect(() => {
    let alive = true;

    const beat = () => {
      api.post("/music/presence", { track_id: trackRef.current, position_seconds: 0 }).catch(() => {});
    };
    const poll = () => {
      api.get("/music/presence")
        .then(({ data }) => { if (alive) setOthers(data); })
        .catch(() => {});
    };

    beat();
    poll();
    const beatTimer = setInterval(beat, HEARTBEAT_MS);
    const pollTimer = setInterval(poll, POLL_MS);

    // Best-effort clean exit so members vanish immediately on navigation away.
    const onBeforeUnload = () => {
      try {
        navigator.sendBeacon
          ? navigator.sendBeacon(
              `${api.defaults.baseURL}/music/presence`,
              new Blob([JSON.stringify({})], { type: "application/json" })
            )
          : api.delete("/music/presence");
      } catch {}
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      alive = false;
      clearInterval(beatTimer);
      clearInterval(pollTimer);
      window.removeEventListener("beforeunload", onBeforeUnload);
      api.delete("/music/presence").catch(() => {});
    };
  }, []);

  if (others.length === 0) {
    return null; // No one else listening — keep the corner clean.
  }

  return (
    <div className="fixed bottom-5 right-5 z-30 select-none" data-testid="listen-together">
      {open ? (
        <div className="jj-card !p-3 shadow-xl bg-white max-w-xs">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="overline text-burgundy flex items-center gap-1.5 text-[10px]">
              <Users className="h-3 w-3" /> Listening with you · {others.length}
            </div>
            <button onClick={() => setOpen(false)} className="text-[var(--jj-muted)] hover:text-burgundy" aria-label="Hide" data-testid="listen-together-hide">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <ul className="space-y-2">
            {others.slice(0, 6).map((p) => {
              const sameTrack = p.track && currentTrack && p.track.id === currentTrack.id;
              return (
                <li key={p.user.id}>
                  <button
                    type="button"
                    onClick={() => p.track && onJoin?.(p.track)}
                    disabled={!p.track || sameTrack}
                    className="w-full text-left flex items-center gap-2.5 px-2 py-1.5 rounded-xl hover:bg-cream-surface disabled:opacity-60 disabled:cursor-default transition"
                    title={p.track ? `Join ${p.user.name} — ${p.track.title}` : `${p.user.name} is browsing`}
                    data-testid={`listen-together-${p.user.id}`}
                  >
                    <span className="relative shrink-0">
                      <Avatar user={p.user} size={32} />
                      {p.track && (
                        <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-gold flex items-center justify-center ring-2 ring-white">
                          <Music2 className="h-1.5 w-1.5 text-jjblue" strokeWidth={3} />
                        </span>
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs font-medium text-jjblue truncate">{p.user.name}</span>
                      <span className="block text-[10px] text-[var(--jj-muted)] truncate">
                        {p.track
                          ? (sameTrack ? "Same track 🎵" : `Listening to ${p.track.title}`)
                          : "Browsing"}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {others.length > 6 && (
            <div className="text-[10px] text-[var(--jj-muted)] text-center pt-1">
              + {others.length - 6} more
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="relative jj-card !p-2.5 shadow-xl bg-white inline-flex items-center gap-2 hover:bg-cream-surface transition-colors"
          data-testid="listen-together-show"
        >
          <span className="flex -space-x-2">
            {others.slice(0, 3).map((p) => (
              <Avatar key={p.user.id} user={p.user} size={28} ring />
            ))}
          </span>
          <span className="text-xs font-medium text-jjblue">+{others.length} listening</span>
        </button>
      )}
    </div>
  );
}
